<template>
    <figure class="gradient">
        <div class="gradient__toolbar">
            <figcaption class="gradient__text">
                <span
                    class="gradient__input"
                    contenteditable
                    @blur="onTextInput"
                    v-text="gradient.displayName"
                />
            </figcaption>

            <div class="gradient__buttons">
                <button
                    v-show="gradientNew.points.length < MAX_COLOR_STOPS"
                    class="gradient__add"
                    type="button"
                    @click="onAddGradient"
                >
                    <font-awesome-icon class="gradient__add-icon" :icon="['fal', 'plus']" />
                </button>

                <button
                    v-for="(point, pointIndex) in gradientNew.points"
                    :key="point.position"
                    class="gradient__button"
                    :style="{ backgroundColor: getButtonColor(pointIndex) }"
                    type="button"
                    @click.stop="showColorPicker(pointIndex)"
                />

                <div
                    v-show="pickerVisible"
                    v-on-clickaway="hideColorPicker"
                    class="gradient__popup"
                >
                    <header class="gradient__popup-header">
                        <h6 class="gradient__popup-title">
                            Color
                        </h6>

                        <button
                            v-if="gradientNew.points.length > 2"
                            class="gradient__popup-delete"
                            @click="onDeleteGradient"
                        >
                            <font-awesome-icon :icon="['fal', 'trash-alt']" />
                        </button>
                    </header>

                    <gradient-picker
                        class="gradient__popup-picker"
                        :style="gradientPickerStyle"
                        :value="pickerValue"
                        tabindex="0"
                        @input="onColorInput"
                    />
                </div>
            </div>

            <div class="gradient__direction">
                <input
                    class="gradient__direction-input"
                    :value="gradientNew.direction"
                    @input="onDirectionInput"
                >

                <span class="gradient__direction-label">
                    deg
                </span>
            </div>
        </div>

        <div class="gradient__preview" :style="previewStyle">
            <slider-multi :value="colorStops" @input="onColorStopInput" />
        </div>
    </figure>
</template>

<script>
import { Chrome } from '@xcentium/vue-color';
import { mixin as clickaway } from 'vue-clickaway';
import mapFilters from '@/utils/map-filters';
import checkerboardPattern from '@/assets/ui/checkerboard-pattern.png';

export default {
    name: 'settings-gradient',
    components: { 'gradient-picker': Chrome },
    mixins: [clickaway],

    props: {
        index: {
            type: Number,
            required: true,
        },

        gradient: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            MAX_COLOR_STOPS: 5,
            pickerIndex: 0,
            pickerValue: '#000000',
            pickerVisible: false,
            gradientNew: { ...this.gradient },
        };
    },

    computed: {
        colorStops() {
            return this.gradientNew.points.map((point) => {
                return Number(point.position.slice(0, -1));
            });
        },

        gradientPickerStyle() {
            const numFromRight = (this.gradientNew.points.length - 1) - this.pickerIndex;
            const offset = numFromRight * 50;

            return {
                'margin-right': `${ offset }px`,
            };
        },

        previewStyle() {
            const { direction, type } = this.gradientNew;

            return {
                backgroundImage: `${ type }-gradient(${ direction }deg, ${ this.positionString }), url('${ checkerboardPattern }')`,
                backgroundRepeat: 'repeat',
            };
        },

        positionString() {
            const { points } = this.gradientNew;

            let posString = `${ points[0].color } ${ points[0].position }, ${ points[1].color } ${ points[1].position }`;

            for (let i = 2, len = points.length; i < len; i++) {
                posString += `, ${ points[i].color } ${ points[i].position }`;
            }
            return posString;
        },
    },

    methods: {
        ...mapFilters(['paramCase']),

        showColorPicker(pointIndex) {
            this.pickerValue = this.getButtonColor(pointIndex);
            this.pickerIndex = pointIndex;
            this.pickerVisible = true;
        },

        hideColorPicker() {
            this.pickerVisible = false;
        },

        onColorInput(color) {
            this.gradientNew.points[this.pickerIndex].color = `rgba(${ color.rgba.r }, ${ color.rgba.g }, ${ color.rgba.b }, ${ color.rgba.a })`;
            this.update();
        },

        onTextInput(e) {
            this.gradientNew.displayName = e.target.innerText;
            this.update();
        },

        onDirectionInput(e) {
            this.gradientNew.direction = e.target.value;

            if (this.gradientNew.direction < 0) this.gradientNew.direction = 0;
            if (this.gradientNew.direction > 360) this.gradientNew.direction = 360;

            this.update();
        },

        onColorStopInput(value) {
            this.gradientNew.points.forEach((point, index) => {
                point.position = `${ value[index] }%`;
            });

            this.update();
        },

        onAddGradient() {
            const points = [...this.gradientNew.points];

            if (points.length === this.MAX_COLOR_STOPS) return;

            points.unshift({
                position: '0%',
                color: '#000000',
            });

            points.reduce((prevPoint, currPoint) => {
                if (currPoint.position === prevPoint.position) {
                    currPoint.position = `${ Number(prevPoint.position.slice(0, -1)) + 1 }%`;
                }

                return currPoint;
            }, {});

            this.gradientNew.points = points;
            this.update();
        },

        onDeleteGradient() {
            const points = [...this.gradientNew.points];

            if (points.length === this.MAX_COLOR_STOPS) return;

            points.splice(this.pickerIndex, 1);
            this.gradientNew.points = points;
            this.update();
        },

        getButtonColor(index) {
            return this.gradientNew.points[index].color;
        },

        update() {
            this.$emit('input', this.index, this.gradientNew);
        },
    },
};
</script>

<style lang="scss">
.gradient {
    &__toolbar {
        display: flex;
        align-items: center;
        position: relative;
        padding-bottom: 10px;
    }

    &__text {
        font-size: 14px;
        margin-right: auto;
        font-weight: 700;
    }

    &__input {
        @extend %input-display;
        display: inline-block;
        vertical-align: middle;
        text-align: center;
    }

    &__buttons {
        position: relative;
        display: flex;
        align-items: center;
    }

    &__button {
        width: 37px;
        height: 37px;
        outline: 1px solid black;
        margin-right: 20px;
    }

    &__add {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        width: 37px;
        height: 37px;
        transition: all 0.33s;
        margin-right: 20px;
        color: var(--app-color);
        border: 1px solid var(--app-color);

        &:hover {
            cursor: pointer;
        }
    }

    &__add-icon {
        width: 17px;
        margin-bottom: 5px;
        margin-top: 4px;
        opacity: 0.9;
        position: relative;
        top: 1px;
    }

    &__preview {
        position: relative;
        height: 50px;
    }

    &__popup {
        display: flex;
        flex-direction: column;
        color: var(--modal-color);
        background-color: var(--modal-backgroundColor);
        box-shadow: 0 0 12px -7px var(--modal-color);
        border: 1px solid black;
        border-radius: 5px;
        padding: 5px;
        position: absolute;
        top: -1px;
        right: 0;
        z-index: 100;
        width: 250px;
    }

    &__popup-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 13px;
    }

    &__popup-title {
        font-weight: 600;
        padding: 5px 5px 10px;
    }

    &__popup-delete {
        padding: 5px 5px 10px;
        color: var(--form-deleteLinkColor);

        &:hover {
            color: var(--form-deleteLinkHoverColor);
        }
    }

    &__popup-picker {
        box-shadow: none !important;
        width: 100% !important;
    }

    &__direction {
        display: flex;
        align-items: center;
        font-size: 14px;
    }

    &__direction-input {
        text-align: right;
        padding: 2px 4px;
        border: 1px solid transparent;
        border-bottom-color: var(--form-inputBorderColor);
        transition: border-color 0.4s ease;
        width: 35px;
        margin-left: 20px;
        font-weight: 700;
        background-color: inherit;

        &:focus {
            border-color: var(--form-inputBorderColor);
        }
    }

    .vc-chrome {
        color: inherit !important;
        background-color: inherit !important;

        .vc-chrome-hue-wrap {
            margin-top: 10px;
            width: 95%;
            left: 5%;
        }

        .vc-chrome-alpha-wrap {
            display: none;
        }

        .vc-chrome-toggle-icon {
            margin: 1px;

            path {
                fill: var(--modal-color);
            }
        }

        .vc-chrome-toggle-icon-highlight {
            display: none;
        }

        .vc-chrome-fields .vc-input__label {
            color: inherit !important;
        }

        .vc-chrome-body {
            background-color: inherit;
        }
    }

    .vue-slider {
        @extend %fill;
        margin-left: -6px;
    }

    .vue-slider-rail {
        top: -12px;
        background-color: transparent;
    }

    .vue-slider-process {
        background-color: transparent;
    }

    .vue-slider-mark {
        display: none;
    }

    .vue-slider-dot {
        &:before {
            content: "";
            display: block;
            width: 2px;
            height: 51px;
            background-color: var(--app-color);
            position: absolute;
            top: 9px;
            left: 9px;
        }

        &:hover {
            cursor: pointer;
        }
    }

    .vue-slider-dot-tooltip {
        margin-left: 3px;
    }

    .vue-slider-dot-handle {
        border-top: 10px solid var(--app-color);
        border-right: 10px solid transparent;
        border-left: 10px solid transparent;
        background-color: transparent;
    }

    .vue-slider-dot-tooltip-inner {
        background-color: var(--app-color);
        color: var(--app-backgroundColor);
    }
}
</style>
